import React, { useState } from "react";
import { Mail, Lock, Chrome, ShieldCheck, Loader2, Check, Clock, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

function Field({ icon: Icon, ...props }) {
  return (
    <div className="flex items-center rounded-xl border border-am-border bg-am-surface2 px-3 focus-within:border-am-border-strong">
      <Icon className="h-4 w-4 text-am-text3" />
      <input {...props} className="w-full bg-transparent px-3 py-2.5 text-sm text-white placeholder:text-am-text3 outline-none" />
    </div>
  );
}

export function AccountSecurity({ section }) {
  const { user, changeEmail, cancelEmailChange, setPassword, resendVerification, checkAuth } = useAuth();
  const [emailOpen, setEmailOpen] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);

  const [pwOpen, setPwOpen] = useState(false);
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  const hasPassword = !!user?.has_password;
  const isGoogle = user?.auth_provider === "google";
  const pending = user?.pending_email;

  const submitEmail = async (e) => {
    e.preventDefault();
    setEmailBusy(true);
    try {
      await changeEmail(newEmail.trim());
      toast("Verification email sent", { description: `Confirm it from ${newEmail.trim()} to switch your email.` });
      setEmailOpen(false); setNewEmail("");
      await checkAuth();
    } catch (err) {
      toast("Couldn't change email", { description: err.message });
    } finally { setEmailBusy(false); }
  };

  const cancelPending = async () => {
    try { await cancelEmailChange(); await checkAuth(); toast("Email change canceled"); }
    catch (err) { toast(err.message); }
  };

  const submitPw = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) { toast("Password must be at least 6 characters."); return; }
    if (newPw !== confirmPw) { toast("Passwords don't match."); return; }
    setPwBusy(true);
    try {
      const r = await setPassword(newPw, hasPassword ? curPw : undefined);
      toast(r.created ? "Password created" : "Password updated", { description: r.created ? "You can now log in with email + password too." : "Use your new password next time you log in." });
      setPwOpen(false); setCurPw(""); setNewPw(""); setConfirmPw("");
      await checkAuth();
    } catch (err) {
      toast("Couldn't save password", { description: err.message });
    } finally { setPwBusy(false); }
  };

  const showEmail = section === "account";
  const showPassword = true;

  return (
    <div className="space-y-5" data-testid="account-security">
      {showEmail && (
        <div className="rounded-xl border border-am-border bg-am-bg/60 p-4">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-white"><Mail className="h-4 w-4 text-aura-cyan" /> Email</div>
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="truncate text-sm text-white" data-testid="current-email">{user?.email}</div>
              <div className="mt-0.5 flex items-center gap-1.5 text-xs">
                {user?.email_verified
                  ? <span className="flex items-center gap-1 text-aura-mint"><Check className="h-3 w-3" /> Verified</span>
                  : <span className="flex items-center gap-1 text-aura-gold">Unverified
                      <button onClick={() => resendVerification().then(() => toast("Verification email sent")).catch((e) => toast(e.message))} className="underline">Resend</button>
                    </span>}
              </div>
            </div>
            {!emailOpen && <button onClick={() => setEmailOpen(true)} data-testid="change-email-btn" className="shrink-0 rounded-full border border-am-border-strong px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/5">Change</button>}
          </div>

          {pending && (
            <div className="mt-3 flex items-center justify-between gap-2 rounded-lg border border-aura-gold/25 bg-aura-gold/[0.06] px-3 py-2 text-xs text-am-text2" data-testid="pending-email">
              <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-aura-gold" /> Waiting for verification of <span className="font-semibold text-white">{pending}</span></span>
              <button onClick={cancelPending} className="flex items-center gap-1 font-semibold text-white hover:text-aura-magenta"><X className="h-3 w-3" /> Cancel</button>
            </div>
          )}

          {emailOpen && (
            <form onSubmit={submitEmail} className="mt-3 space-y-2" data-testid="change-email-form">
              <Field icon={Mail} type="email" required placeholder="New email address" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} data-testid="new-email-input" />
              <p className="text-xs text-am-text3">We'll send a link to the new address. Your current email keeps working until you confirm the new one.</p>
              <div className="flex gap-2">
                <button type="submit" disabled={emailBusy} data-testid="send-change-email" className="flex flex-1 items-center justify-center gap-1.5 rounded-full aura-gradient py-2.5 text-sm font-bold text-black disabled:opacity-60">
                  {emailBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send verification"}
                </button>
                <button type="button" onClick={() => { setEmailOpen(false); setNewEmail(""); }} className="rounded-full border border-am-border px-4 text-sm font-semibold text-am-text2 hover:text-white">Cancel</button>
              </div>
            </form>
          )}
        </div>
      )}

      {showEmail && (
        <div className="rounded-xl border border-am-border bg-am-bg/60 p-4">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-white"><ShieldCheck className="h-4 w-4 text-aura-mint" /> Sign-in methods</div>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-am-text2"><Chrome className="h-4 w-4" /> Google {isGoogle ? <span className="text-aura-mint">· connected</span> : <span className="text-am-text3">· available</span>}</div>
            <div className="flex items-center gap-2 text-sm text-am-text2"><Lock className="h-4 w-4" /> Email &amp; password {hasPassword ? <span className="text-aura-mint">· enabled</span> : <span className="text-am-text3">· not set</span>}</div>
          </div>
        </div>
      )}

      {showPassword && (
        <div className="rounded-xl border border-am-border bg-am-bg/60 p-4">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-white"><Lock className="h-4 w-4 text-aura-magenta" /> Password</div>
          {!pwOpen ? (
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-am-text2">{hasPassword ? "Change your password." : "Add a password so you can also log in with email."}</p>
              <button onClick={() => setPwOpen(true)} data-testid="password-btn" className="shrink-0 rounded-full border border-am-border-strong px-3 py-1.5 text-xs font-semibold text-white hover:bg-white/5">{hasPassword ? "Change" : "Create"}</button>
            </div>
          ) : (
            <form onSubmit={submitPw} className="space-y-2" data-testid="password-form">
              {hasPassword && <Field icon={Lock} type="password" required placeholder="Current password" value={curPw} onChange={(e) => setCurPw(e.target.value)} data-testid="current-password-input" />}
              <Field icon={Lock} type="password" required minLength={6} placeholder="New password" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="new-password-input" />
              <Field icon={Lock} type="password" required placeholder="Confirm new password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} data-testid="confirm-password-input" />
              <div className="flex gap-2">
                <button type="submit" disabled={pwBusy} data-testid="save-password" className="flex flex-1 items-center justify-center gap-1.5 rounded-full aura-gradient py-2.5 text-sm font-bold text-black disabled:opacity-60">
                  {pwBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : (hasPassword ? "Update password" : "Create password")}
                </button>
                <button type="button" onClick={() => { setPwOpen(false); setCurPw(""); setNewPw(""); setConfirmPw(""); }} className="rounded-full border border-am-border px-4 text-sm font-semibold text-am-text2 hover:text-white">Cancel</button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
